import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { NavbarModule } from '@/common/navbar/navbar.module';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { TransportSegmentsApiService } from '@/bag/transport-segments/transport-segments.api.service';
import {
	Segment,
	TransportSegmentsFilters,
	TransportSegmentsOrdering,
} from '@/bag/transport-segments/transport-segments.model';
import { TransportTermList } from '@/bag/bag-transport-term/bag-transport-term.model';
import { ProcessBagTransportTermData } from '@/bag/bag-transport-term/process-bag-transport-term-data';
import { BagTransportTermTableComponent } from '@/bag/bag-transport-term/bag-transport-term-table/bag-transport-term-table.component';
import { ActivatedRoute, Router } from '@angular/router';
import { getPolishPaginatorIntl } from '@/common/get-polish-paginator-intl';
import { SegmentFilesDialogComponent } from '@/bag/transport-segments/segment-files-dialog/segment-files-dialog.component';
import { SegmentAssignTourismEventDialogComponent } from '@/bag/transport-segments/segment-assign-tourism-event-dialog/segment-assign-tourism-event-dialog.component';
import { TransportSegmentsFiltersComponent } from '@/bag/transport-segments/transport-segments-filters/transport-segments-filters.component';

type Column = keyof Pick<Segment, 'id' | 'name' | 'transportTerms'> | 'dateRange' | 'edit';

const EMPTY_FILTERS: TransportSegmentsFilters = {
	search: null,
	departure_date_before: null,
	departure_date_after: null,
	return_date_before: null,
	return_date_after: null,
	ordering: 'name',
};

const ALLOWED_ORDERING_VALUES = new Set(['name', '-name', 'date_from', '-date_from', 'date_to', '-date_to']);

@Component({
	selector: 'app-transport-segments',
	standalone: true,
	imports: [
		CommonModule,
		NavbarModule,
		MatTableModule,
		MatPaginatorModule,
		MatButtonModule,
		MatTooltipModule,
		MatProgressSpinnerModule,
		BagTransportTermTableComponent,
		TransportSegmentsFiltersComponent,
	],
	providers: [{ provide: MatPaginatorIntl, useValue: getPolishPaginatorIntl() }],
	templateUrl: './transport-segments.component.html',
	styleUrls: ['./transport-segments.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransportSegmentsComponent implements OnInit {
	readonly tableParameters = signal({
		statuses: [
			{ value: 100, viewValue: '100%' },
			{ value: 75, viewValue: '75%' },
			{ value: 50, viewValue: '50%' },
			{ value: 25, viewValue: '25%' },
			{ value: 0, viewValue: '0%' },
		],
		organizationStatuses: [] as Array<{ id: number; name: string }>,
	});
	readonly segments = signal<Segment[]>([]);
	readonly page = signal(0);
	readonly pageSize = signal(10);
	readonly allItemsSize = signal(0);
	readonly responsibleUserHasChanged = signal(false);
	readonly columnsToDisplay: Column[] = ['id', 'name', 'dateRange', 'transportTerms'];
	readonly isLoading = signal(false);
	readonly activeFilters = signal<TransportSegmentsFilters>(EMPTY_FILTERS);
	readonly columnsToDisplayWithExpand: Array<Column | 'expand'> = [...this.columnsToDisplay, 'expand'];

	expandedElement: Segment | null = null;
	readonly segmentsCache = signal<Record<string, Segment[]>>({});
	readonly segmentTermsMap = signal<Record<number, TransportTermList[]>>({});
	readonly loadingSegmentTermsMap = signal<Record<number, boolean>>({});
	private readonly transportSegmentsApiService = inject(TransportSegmentsApiService);
	private readonly datePipe = inject(DatePipe);
	private readonly destroyRef = inject(DestroyRef);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly matDialog = inject(MatDialog);

	constructor() {
		this.activeFilters.set(this.getFiltersFromQueryParams());
	}

	ngOnInit(): void {
		this.loadOrganizationStatuses();
		this.loadSegments();
	}

	onFiltersApplied(filters: TransportSegmentsFilters): void {
		this.activeFilters.set(filters);
		this.page.set(0);
		this.expandedElement = null;
		this.segmentsCache.set({});
		this.updateQueryParams(filters);
		this.loadSegments();
	}

	pageChanged(event: PageEvent): void {
		this.page.set(event.pageIndex);
		this.pageSize.set(event.pageSize);
		this.expandedElement = null;
		this.loadSegments();
	}

	isExpanded(element: Segment): boolean {
		return this.expandedElement === element;
	}

	toggle(element: Segment): void {
		if (this.isExpanded(element)) {
			this.expandedElement = null;
			return;
		}
		this.expandedElement = element;
		this.loadSegmentTerms(element.id);
	}

	getSegmentTerms(segmentId: number): TransportTermList[] {
		return this.segmentTermsMap()[segmentId] ?? [];
	}

	isSegmentTermsLoading(segmentId: number): boolean {
		return !!this.loadingSegmentTermsMap()[segmentId];
	}

	deadlineChanged(): void {
		const expandedElement = this.expandedElement;
		if (!expandedElement) {
			return;
		}
		this.segmentTermsMap.update((current) => {
			const next = { ...current };
			delete next[expandedElement.id];
			return next;
		});
		this.loadSegmentTerms(expandedElement.id);
	}

	onResponsibleUserHasChanged(value: boolean): void {
		this.responsibleUserHasChanged.set(value);
	}

	openAddSegmentPage(): void {
		const urlTree = this.router.createUrlTree(['/transport-segments/add']);
		const url = this.router.serializeUrl(urlTree);
		window.open(url, '_blank');
	}

	openEditSegmentPage(segmentId: number): void {
		const urlTree = this.router.createUrlTree(['/transport-segments', segmentId]);
		const url = this.router.serializeUrl(urlTree);
		window.open(url, '_blank');
	}

	openSegmentListsPage(segmentId: number): void {
		const urlTree = this.router.createUrlTree(['/segment', segmentId, 'list']);
		const url = this.router.serializeUrl(urlTree);
		window.open(url, '_blank');
	}

	openSegmentFilesDialog(segmentId: number): void {
		this.matDialog.open(SegmentFilesDialogComponent, {
			data: segmentId,
			width: '70%',
			maxWidth: '900px',
		});
	}

	openAssignTourismEventDialog(segment: Segment): void {
		this.matDialog
			.open(SegmentAssignTourismEventDialogComponent, {
				data: segment,
				width: '600px',
				maxWidth: '90vw',
			})
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((saved) => {
				if (!saved) {
					return;
				}
				this.segmentTermsMap.set({});
				this.loadingSegmentTermsMap.set({});
				this.expandedElement = null;
			});
	}

	private loadSegmentTerms(segmentId: number): void {
		if (this.segmentTermsMap()[segmentId]) {
			return;
		}

		this.loadingSegmentTermsMap.update((current) => ({
			...current,
			[segmentId]: true,
		}));

		this.transportSegmentsApiService
			.getTransportTermsBySegment(segmentId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				const terms = new ProcessBagTransportTermData(this.datePipe).afterConvertData(result, []);
				this.segmentTermsMap.update((current) => ({
					...current,
					[segmentId]: terms,
				}));
				this.loadingSegmentTermsMap.update((current) => ({
					...current,
					[segmentId]: false,
				}));
			});
	}

	private loadSegments(): void {
		const page = this.page();
		const pageSize = this.pageSize();
		const filters = this.activeFilters();
		const cacheKey = this.getCacheKey(page, pageSize, filters);
		const cachedSegments = this.segmentsCache()[cacheKey];
		if (cachedSegments) {
			this.segments.set(cachedSegments);
			return;
		}

		const offset = page * pageSize;
		this.isLoading.set(true);
		this.transportSegmentsApiService
			.getSegments(pageSize, offset, filters)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.isLoading.set(false)),
			)
			.subscribe((result) => {
				this.segments.set(result.results);
				this.allItemsSize.set(result.count);
				this.segmentsCache.update((current) => ({
					...current,
					[cacheKey]: result.results,
				}));
			});
	}

	private loadOrganizationStatuses(): void {
		this.transportSegmentsApiService
			.getOrganizationStatuses()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				this.tableParameters.update((current) => ({
					...current,
					organizationStatuses: result.results,
				}));
			});
	}

	private getFiltersFromQueryParams(): TransportSegmentsFilters {
		const queryParams = this.route.snapshot.queryParams;
		return {
			search: this.normalizeTextQueryParam(queryParams['search']),
			departure_date_before: this.normalizeDateQueryParam(queryParams['departure_date_before']),
			departure_date_after: this.normalizeDateQueryParam(queryParams['departure_date_after']),
			return_date_before: this.normalizeDateQueryParam(queryParams['return_date_before']),
			return_date_after: this.normalizeDateQueryParam(queryParams['return_date_after']),
			ordering: this.normalizeOrderingQueryParam(queryParams['ordering']),
		};
	}

	private normalizeTextQueryParam(value: unknown): string | null {
		if (typeof value !== 'string') {
			return null;
		}
		const normalizedValue = value.trim();
		if (!normalizedValue) {
			return null;
		}
		return normalizedValue;
	}

	private normalizeDateQueryParam(value: unknown): string | null {
		if (typeof value !== 'string') {
			return null;
		}
		if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
			return null;
		}
		return value;
	}

	private normalizeOrderingQueryParam(value: unknown): TransportSegmentsOrdering {
		if (typeof value === 'string' && ALLOWED_ORDERING_VALUES.has(value)) {
			return value as TransportSegmentsOrdering;
		}
		return EMPTY_FILTERS.ordering ?? 'name';
	}

	private updateQueryParams(filters: TransportSegmentsFilters): void {
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: {
				search: filters.search,
				departure_date_before: filters.departure_date_before,
				departure_date_after: filters.departure_date_after,
				return_date_before: filters.return_date_before,
				return_date_after: filters.return_date_after,
				ordering: filters.ordering,
			},
			queryParamsHandling: 'merge',
			replaceUrl: true,
		});
	}

	private getCacheKey(page: number, pageSize: number, filters: TransportSegmentsFilters): string {
		return `${page}-${pageSize}-${JSON.stringify(filters)}`;
	}
}
